import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Modal from 'react-modal';
import StudentNav from "./StudentNav";

Modal.setAppElement('#root');

const StudentDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { student } = location.state || {};
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [currentSubject, setCurrentSubject] = useState(null);

  // Early validation of student data
  useEffect(() => {
    if (!student || !student.id || !student.enrollments) {
      navigate('/student-login');
      return;
    }
  }, [student, navigate]);

  useEffect(() => {
    if (!student || !student.enrollments || !student.enrollments.length) {
      setClasses({});
      setLoading(false);
      return;
    }

    const fetchClassesAndSubjects = async () => {
      setLoading(true);
      try {
        // Fetch current term data
        const currentTermDoc = await getDoc(doc(db, "current-term", "current"));
        if (!currentTermDoc.exists()) {
          setClasses({});
          setLoading(false);
          return;
        }
        const currentTermData = currentTermDoc.data();
        const currentSessionId = currentTermData?.sessionId;
        const currentTermId = currentTermData?.termId;

        if (!currentSessionId || !currentTermId) {
          setClasses({});
          setLoading(false);
          return;
        }

        // Fetch sections data
        const sectionsQuery = query(collection(db, "sections"));
        const sectionsSnapshot = await getDocs(sectionsQuery);
        const sectionsData = Object.fromEntries(
          sectionsSnapshot.docs.map(doc => [doc.id, doc.data().name])
        );

        // Fetch subjects data
        const subjectsSnap = await getDocs(collection(db, "subjects"));
        const subjectsMap = Object.fromEntries(
          subjectsSnap.docs.map(doc => [doc.id, doc.data().name])
        );

        // Fetch all class documents for the student's enrollments
        const classDataPromises = student.enrollments.map(async (enrollment) => {
          if (!enrollment.classId) {
            return null;
          }
          const classRef = doc(db, 'classes', enrollment.classId);
          const classSnap = await getDoc(classRef);
          return { enrollment, classSnap };
        });

        const classResults = (await Promise.all(classDataPromises)).filter(result => result !== null);

        // Filter enrollments where the class matches the current session and term
        const validEnrollments = classResults
          .filter(({ classSnap }) => {
            if (!classSnap.exists()) {
              return false;
            }
            const classData = classSnap.data();
            const isCurrentTerm = classData.session === currentSessionId && classData.term === currentTermId;
            return isCurrentTerm;
          })
          .map(({ enrollment, classSnap }) => ({
            enrollment,
            classData: classSnap.data(),
            classId: classSnap.id
          }));

        if (!validEnrollments.length) {
          setClasses({});
          setLoading(false);
          return;
        }

        // Process valid enrollments to build class and subject data
        const classData = validEnrollments.map(({ enrollment, classData, classId }) => {
          const classSubjects = classData.subjects || [];
          const studentSubjectIds = enrollment.subjects?.map(sub => sub.subjectId) || [];

          const enrolledSubjects = classSubjects
            .filter(sub => studentSubjectIds.includes(sub.id))
            .map(sub => ({
              id: sub.id,
              name: subjectsMap[sub.id] || 'Unknown',
              questionSetId: sub.questionSetId || '',
              questionCount: sub.questionCount || 0,
              marksPerQuestion: sub.marksPerQuestion || 0,
              activationDate: sub.activationDate || '',
              deactivationDate: sub.deactivationDate || '',
              duration: sub.duration || 0,
              pdfLink: sub.pdfLink || ''
            }));

          return {
            classId,
            className: classData.name || 'Unknown Class',
            sectionId: classData.section || '',
            sectionName: sectionsData[classData.section] || 'Unknown Section',
            subjects: enrolledSubjects
          };
        });

        // Group classes by section
        const groupedClasses = classData.reduce((acc, cls) => {
          if (!acc[cls.sectionName]) {
            acc[cls.sectionName] = [];
          }
          acc[cls.sectionName].push(cls);
          return acc;
        }, {});

        setClasses(groupedClasses);
      } catch (error) {
        console.error("Error fetching classes and subjects:", error);
        setClasses({});
      } finally {
        setLoading(false);
      }
    };

    fetchClassesAndSubjects();
  }, [student]);

  const isCBTActive = (activationDate, deactivationDate) => {
    if (!activationDate || !deactivationDate) return false;
    const now = new Date();
    const activation = new Date(activationDate);
    const deactivation = new Date(deactivationDate);
    return now >= activation && now <= deactivation;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date).replace(',', '');
  };

  const openDetailsModal = (subject) => {
    setCurrentSubject(subject);
    setIsDetailsModalOpen(true);
  };

  const openPdf = (pdfLink) => {
    // Encode the pdfLink to handle special characters in the URL
    const encodedPdfLink = encodeURIComponent(pdfLink);
    navigate(`/pdf/${encodedPdfLink}`, { state: { student } });
  };

  const handleTakeExam = async (subjectId) => {
    try {
      const currentTermDoc = await getDoc(doc(db, "current-term", "current"));
      if (!currentTermDoc.exists()) {
        return;
      }

      const { sessionId: currentSessionId, termId: currentTermId } = currentTermDoc.data();

      if (!currentSessionId || !currentTermId) {
        return;
      }

      // Find full subject and its classId from current dashboard state
      let fullSubject = null;
      let currentClassId = null;

      Object.values(classes).forEach(sectionClasses => {
        sectionClasses.forEach(cls => {
          const found = cls.subjects.find(s => s.id === subjectId);
          if (found) {
            fullSubject = found;
            currentClassId = cls.classId;
          }
        });
      });

      if (!fullSubject || !fullSubject.questionSetId || !currentClassId) {
        return;
      }

      const studentRef = doc(db, 'students', student.id);
      const studentSnap = await getDoc(studentRef);
      const freshStudent = { id: studentSnap.id, ...studentSnap.data() };

      navigate(`/exam/${subjectId}`, { 
        state: { 
          student: freshStudent,
          currentSessionId,
          currentTermId,
          subjectDetails: fullSubject,
          classId: currentClassId
        } 
      });
    } catch (error) {
    }
  };

  if (!student || !student.id || !student.firstName || !student.lastName) {
    return (
      <div className="column align-center">
        <p className="error-message size3 align-center justify-center">No student data available</p>
        <Link to="/student-login" className="back-link">
          <button className="login-button">Back to Login</button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <StudentNav />
      <div className="student-dashboard-container column align-center">
        <div className="student-name">
          <span className="bold size5">{student.firstName} {student.middleName} {student.lastName}</span><br />
          <span className="size5">{student.admissionNumber}</span>
        </div>

        <p className="align-center justify-center bold">ENROLLED CLASSES AND SUBJECTS</p>
        {loading ? (
          <p className="loading-text">Loading classes and subjects...</p>
        ) : Object.keys(classes).length === 0 ? (
          <p className="male-female-stat">No classes enrolled for the current term.</p>
        ) : (
          <div className="scroll-fixed scroll-fixed-questions">
            {Object.entries(classes).map(([sectionName, classesInSection]) => (
              <div key={sectionName} className="section-group">
                <h3 className="subjects-section-name">{sectionName} Section</h3>
                {classesInSection.map(cls => (
                  <div key={cls.classId} className="class-section">
                    <p className="size5 bold caps subjects-section-class">
                      {cls.className} ({cls.sectionName})
                    </p>
                    <div className="class-list class-list-d scrollable">
                      {cls.subjects.length === 0 ? (
                        <p className="male-female-stat">No subjects enrolled for this class.</p>
                      ) : (
                        cls.subjects.map((subject, index) => (
                          <div key={subject.id} className="class-item">
                            <p>
                              <span className="size5 bold">{index + 1}. </span>
                              <span>{subject.name}</span><br />
                              {subject.questionSetId && (
                                <>
                                  <span 
                                    className={`size5 font-gray caps bold ${isCBTActive(subject.activationDate, subject.deactivationDate) ? 'clickable' : ''}`} 
                                    style={{ cursor: isCBTActive(subject.activationDate, subject.deactivationDate) ? 'pointer' : 'default' }}
                                    onClick={() => isCBTActive(subject.activationDate, subject.deactivationDate) && handleTakeExam(subject.id)}
                                  >
                                    CBT Set
                                  </span><br />
                                  <span className="size5 font-gray bold">Start: {formatDate(subject.activationDate)}</span>
                                </>
                              )}
                            </p>
                            <div className="row align-center justify-center gap-15">
                              {subject.questionSetId && (
                                <button 
                                  className="list-btn"
                                  onClick={() => handleTakeExam(subject.id)}
                                  disabled={!subject.questionSetId || !isCBTActive(subject.activationDate, subject.deactivationDate)}
                                  title="Take Exam"
                                >
                                  <i className="fas fa-pen"></i>
                                </button>
                              )}
                              {subject.pdfLink && (
                                <button 
                                  className="list-btn"
                                  onClick={() => openPdf(subject.pdfLink)}
                                  title="View PDF"
                                >
                                  <i className="fas fa-file-pdf"></i>
                                </button>
                              )}
                              <button 
                                className="list-btn" 
                                onClick={() => openDetailsModal(subject)}
                                title="View Details"
                              >
                                <i className="fas fa-info-circle"></i>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subject Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen}
        onRequestClose={() => setIsDetailsModalOpen(false)}
        contentLabel="Subject Details"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <p className="bold size5 caps">Subject Details</p>
        {currentSubject && (
          <div className="column">
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <p><strong>Subject:</strong> {currentSubject.name}</p>
              {currentSubject.questionSetId ? (
                <>
                  <p><strong>CBT Assigned:</strong> Yes</p>
                  <p><strong>Number of Questions:</strong> {currentSubject.questionCount}</p>
                  <p><strong>Marks per Question:</strong> {currentSubject.marksPerQuestion}</p>
                  <p><strong>Total Marks:</strong> 60</p>
                  <p><strong>Duration:</strong> {currentSubject.duration ? `${currentSubject.duration} minutes` : 'Not set'}</p>
                  <p><strong>Activation Date:</strong> {formatDate(currentSubject.activationDate)}</p>
                  <p><strong>Deactivation Date:</strong> {formatDate(currentSubject.deactivationDate)}</p>
                  <p><strong>Status:</strong> {isCBTActive(currentSubject.activationDate, currentSubject.deactivationDate) ? 'Started' : 'Not yet started'}</p>
                </>
              ) : (
                <p><strong>CBT Assigned:</strong> No</p>
              )}
              <p><strong>PDF Available:</strong> {currentSubject.pdfLink ? 'Yes' : 'No'}</p>
              {currentSubject.pdfLink && (
                <p><strong>PDF Link:</strong> {currentSubject.pdfLink}</p>
              )}
            </div>
            <div className="row align-center justify-center gap-1">
              <button 
                className="login-button" 
                type="button" 
                onClick={() => setIsDetailsModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default StudentDashboard;